import { createBrowserInspector } from '@statelyai/inspect';
import { createActorContext } from '@xstate/react';
import config from 'config';
import { getAppointmentTypeFromURL, getCalendarIDFromURL } from 'domains/signup/data/queryParams';
import { signupMachineV2, SignupState } from 'domains/signupv2/Parent/signupMachineV2';
import { signupV2StateChangeListener } from 'domains/signupv2/tracking/mixpanelTracking';
import { isArray } from 'lodash';
import { useEffectOnce } from 'react-use';

const shouldUseInspector = config.get('env') === 'development' && config.get('xstateDebugger') === 'true';
const inspect = shouldUseInspector ? createBrowserInspector().inspect : undefined;

export const SignupContextV2 = createActorContext(signupMachineV2, {
  inspect,
});

type WithSignupContext = <P = Record<string, unknown>>(Component: React.FC<P>, fromScreener?: boolean) => React.FC<P>;

const appointmentType = getAppointmentTypeFromURL();
const calendarID = getCalendarIDFromURL();

/** Needs to be a component in order to access the nested context */
const MixpanelSubscriber = () => {
  const actorRef = SignupContextV2.useActorRef();

  useEffectOnce(() => {
    return actorRef.subscribe(({ value }) => {
      signupV2StateChangeListener(value as SignupState);
    }).unsubscribe;
  });

  return null;
};

export const withSignupProviderV2: WithSignupContext = Component => {
  return props => {
    return (
      <SignupContextV2.Provider
        options={{
          input: {
            appointmentType,
            calendarID,
          },
        }}
      >
        <MixpanelSubscriber />
        <Component {...props} />
      </SignupContextV2.Provider>
    );
  };
};

export const withSignupMachineStateV2 = (Component: React.FC, matchingState: SignupState | SignupState[]) => {
  return (props: Record<string, unknown>) => {
    const matchesState = SignupContextV2.useSelector(state => {
      if (isArray(matchingState) && matchingState.some(s => state.matches(s))) {
        return true;
      }

      if (state.matches(matchingState as SignupState)) {
        return true;
      }

      return false;
    });

    if (matchesState) {
      return <Component {...props} />;
    }
    return null;
  };
};
